import { Identifier } from 'src/types/utilityTypes';

import { Cart } from '../cart/_types';

import { Product, ProductVariant } from './_types';

// Helper function to compare option values objects in an order-independent way
export const compareOptionValues = (
  obj1: Record<string, string> | undefined,
  obj2: Record<string, string> | undefined
): boolean => {
  if (!obj1 && !obj2) return true;
  if (!obj1 || !obj2) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => obj1[key] === obj2[key]);
};

// Check if all variants are out of stock for a product
export const isProductOutOfStock = (
  product: Product,
  publishingChannel = 'checkout',
  locationId?: Identifier,
  cart?: Cart | null
): boolean => {
  if (!product.productVariants || product.productVariants.length === 0) {
    // For products without variants, check the product itself
    return false;
  }

  // Filter variants available for checkout
  const variants = product.productVariants.filter((variant) =>
    variant.publishingChannels?.includes(publishingChannel)
  );

  // If no checkout variants, consider out of stock
  if (variants.length === 0) {
    return true;
  }

  // Check if ALL checkout variants are out of stock
  return variants.every((variant) =>
    isVariantOutOfStock(variant, locationId, cart)
  );
};

// Check if a specific variant is out of stock
export const isVariantOutOfStock = (
  variant: ProductVariant,
  locationId?: Identifier,
  cart?: Cart | null
): boolean => {
  // Always check if inventory is denied at the variant level
  if (!locationId) return true;
  if (!variant.trackInventory) return false;
  if (!variant.inventoryDenied) return false;

  // If locationId is provided, also check inventory levels for that location
  if (locationId && variant.inventoryLevels) {
    const locationInventory = variant.inventoryLevels.find(
      (level) => level.locationId.toString() === locationId.toString()
    );

    // If we have inventory data for this location, check if available count is 0 or less
    if (locationInventory) {
      let availableCount = locationInventory.available;

      // If cart is provided and inventory tracking is enabled, subtract cart quantities
      if (cart && variant.trackInventory) {
        const cartQuantity = cart.cartItems
          .filter(
            (item) =>
              item.itemType === 'ProductVariant' &&
              item.itemId?.toString() === variant.id.toString()
          )
          .reduce((total, item) => total + item.quantity, 0);

        availableCount -= cartQuantity;
      }

      return availableCount <= 0;
    }

    // If no inventory level exists for this location, consider it out of stock
    return true;
  }

  // For variants without location-specific checking, only use inventoryDenied
  return false;
};

// Helper function to create a stable key for option values (for Map keys)
export const createOptionValuesKey = (
  optionValues: Record<string, string> | undefined
): string => {
  if (!optionValues) return '';

  // Sort keys to ensure consistent ordering
  const sortedKeys = Object.keys(optionValues).sort();
  const sortedEntries = sortedKeys.map((key) => `${key}:${optionValues[key]}`);
  return sortedEntries.join('|');
};

// Get the maximum allowed quantity for a variant based on available stock
export const getMaxAllowedQuantity = (
  variant: ProductVariant,
  locationId?: Identifier,
  cart?: Cart | null,
  currentCartItemIndex?: number
): number => {
  // If inventory tracking is disabled, allow unlimited quantity
  if (!variant.trackInventory) return Infinity;

  // If no location is provided, can't determine stock levels
  if (!locationId) return 0;

  // If inventory is denied, no quantity allowed
  if (!variant.inventoryDenied) return Infinity;

  // Find inventory level for the specified location
  const locationInventory = variant.inventoryLevels?.find(
    (level) => level.locationId.toString() === locationId.toString()
  );

  // If no inventory level exists for this location, no quantity allowed
  if (!locationInventory) return 0;

  let availableCount = locationInventory.available;

  // If cart is provided, subtract existing cart quantities for this variant
  if (cart) {
    const cartQuantity = cart.cartItems
      .filter((item, index) => {
        // Exclude the current item being updated to avoid double-counting
        const isCurrentItem =
          currentCartItemIndex !== undefined && index === currentCartItemIndex;
        return (
          !isCurrentItem &&
          item.itemType === 'ProductVariant' &&
          item.itemId?.toString() === variant.id.toString()
        );
      })
      .reduce((total, item) => total + item.quantity, 0);

    availableCount -= cartQuantity;
  }

  return Math.max(0, availableCount);
};

// Validate if a requested quantity is available for a variant
export const isQuantityAvailable = (
  variant: ProductVariant,
  requestedQuantity: number,
  locationId?: Identifier,
  cart?: Cart | null,
  currentCartItemIndex?: number
): boolean => {
  const maxAllowed = getMaxAllowedQuantity(
    variant,
    locationId,
    cart,
    currentCartItemIndex
  );
  return requestedQuantity <= maxAllowed;
};

// Constrain a quantity to the maximum available stock
export const constrainQuantityToStock = (
  variant: ProductVariant,
  requestedQuantity: number,
  locationId?: Identifier,
  cart?: Cart | null,
  currentCartItemIndex?: number
): number => {
  const maxAllowed = getMaxAllowedQuantity(
    variant,
    locationId,
    cart,
    currentCartItemIndex
  );
  return Math.min(requestedQuantity, maxAllowed);
};

// Get a descriptive message about stock availability
export const getStockAvailabilityMessage = (
  variant: ProductVariant,
  locationId?: Identifier,
  cart?: Cart | null,
  currentCartItemIndex?: number
): string | null => {
  if (!variant.trackInventory) return null;

  const maxAllowed = getMaxAllowedQuantity(
    variant,
    locationId,
    cart,
    currentCartItemIndex
  );

  if (maxAllowed === 0) {
    return 'Out of stock';
  } else if (maxAllowed === 1) {
    return 'Only 1 left';
  } else if (maxAllowed <= 5) {
    return `Only ${maxAllowed} left`;
  }

  return null; // No message needed for items with adequate stock
};

export const getAdjustmentType = (reason: string) => {
  switch (reason) {
    case 'Received':
    case 'Return':
      return 'purchase';
    case 'Transfer In':
    case 'Transfer Out':
      return 'transfer';
    case 'Physical Count':
    case 'Audit':
    case 'Correction':
    case 'Damaged':
    case 'Expired':
    case 'Lost':
    case 'Stolen':
    case 'Shrinkage':
    case 'Found':
    case 'Other':
    default:
      return 'manual';
  }
};

export const stockStatus = (
  available: number,
  lowStockThreshold: number,
  idOnly = false
): string => {
  if (available <= 0) {
    if (idOnly) return 'out_of_stock';

    return 'Out of Stock';
  } else if (available <= lowStockThreshold) {
    if (idOnly) return 'low_stock';

    return 'Low Stock';
  } else {
    if (idOnly) return 'in_stock';

    return 'In Stock';
  }
};

export const stockStatusColor = (status: string): string => {
  switch (status) {
    case 'In Stock':
      return 'green';
    case 'Low Stock':
      return 'yellow';
    case 'Out of Stock':
      return 'red';
    default:
      return 'gray';
  }
};
